import React, { useEffect, useMemo, useState } from 'react';
import {
  X,
  ListChecks,
  Shuffle,
  Dices,
  Sparkles,
  Search,
  Check,
  Trophy,
  Users,
  Globe,
  DoorOpen,
  Play,
  KeyRound,
  Loader2,
  Bot,
} from 'lucide-react';
import type { AutoQuizSource, AutoRoomSettings, FriendSummary } from '../../shared/protocol';
import { MAX_PLAYERS_LIMIT, MIN_PLAYERS_LIMIT, MAX_RANDOM_POOL } from '../../shared/protocol';
import { api, type Profile, type QuizSummary, type AiStatus } from '../lib/api';
import { NumberPadField } from './NumberPadField';
import { StartTimePicker, DEFAULT_START, startAtFromChoice, type StartChoice } from './StartTimePicker';
import { InviteFriendsPicker } from './InviteFriends';

type SourceKind = AutoQuizSource['kind'];

export interface CreateMatchPayload {
  source: AutoQuizSource;
  settings: Partial<AutoRoomSettings>;
  startAt: number | null;
  inviteIds: string[];
}

interface Props {
  profile: Profile;
  friends: FriendSummary[] | null;
  onCreate: (payload: CreateMatchPayload) => void;
  onConfigureAi: () => void;
  onManageFriends: () => void;
  onClose: () => void;
}

const SOURCES: { kind: SourceKind; icon: React.ReactNode; title: string; description: string }[] = [
  { kind: 'pick', icon: <ListChecks className="w-5 h-5" />, title: 'Elegir una', description: 'Tú decides cuál se juega' },
  { kind: 'random', icon: <Dices className="w-5 h-5" />, title: 'Al azar', description: 'Cualquiera del catálogo' },
  { kind: 'randomFrom', icon: <Shuffle className="w-5 h-5" />, title: 'Al azar entre varias', description: 'Marca unas cuantas y se sortea' },
  { kind: 'ai', icon: <Sparkles className="w-5 h-5" />, title: 'Que la cree la IA', description: 'Sobre un tema vuestro o suyo' },
];

/**
 * Crear una partida desde la sala de espera.
 *
 * A diferencia de "Anfitrionar" en una trivia del catálogo, aquí la sala la
 * dirige el propio servidor: quien la crea juega como uno más, nadie tiene que
 * ir pulsando "Siguiente" y la partida arranca cuando la gente está lista o a
 * la hora fijada.
 */
export const CreateMatchModal: React.FC<Props> = ({
  profile,
  friends,
  onCreate,
  onConfigureAi,
  onManageFriends,
  onClose,
}) => {
  const [kind, setKind] = useState<SourceKind>('pick');
  const [picked, setPicked] = useState<QuizSummary | null>(null);
  const [pool, setPool] = useState<QuizSummary[]>([]);
  const [aiTopicMode, setAiTopicMode] = useState<'ours' | 'theirs'>('ours');
  const [topic, setTopic] = useState('');
  const [count, setCount] = useState(10);
  const [aiStatus, setAiStatus] = useState<AiStatus | null>(null);

  const [settings, setSettings] = useState<AutoRoomSettings>({
    gameMode: 'ffa',
    maxPlayers: 20,
    isPublic: true,
    allowLateJoin: true,
    randomizeQuestions: false,
  });
  const [start, setStart] = useState<StartChoice>(DEFAULT_START);
  const [inviteIds, setInviteIds] = useState<string[]>([]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // El teclado numérico emergente maneja su propio Escape.
      if (e.key === 'Escape' && !document.querySelector('[aria-labelledby="numpad-title"]')) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (kind !== 'ai' || aiStatus) return;
    void api
      .get<AiStatus>('/ai/status')
      .then(setAiStatus)
      .catch(() => setAiStatus({ configured: false } as AiStatus));
  }, [kind, aiStatus]);

  const set = <K extends keyof AutoRoomSettings>(key: K, value: AutoRoomSettings[K]) =>
    setSettings((prev) => ({ ...prev, [key]: value }));

  const source: AutoQuizSource | null =
    kind === 'pick'
      ? picked
        ? { kind: 'pick', quizId: picked.id }
        : null
      : kind === 'random'
        ? { kind: 'random' }
        : kind === 'randomFrom'
          ? pool.length >= 2
            ? { kind: 'randomFrom', quizIds: pool.map((q) => q.id) }
            : null
          : aiTopicMode === 'theirs' || topic.trim()
            ? { kind: 'ai', topic: aiTopicMode === 'theirs' ? null : topic.trim(), count }
            : null;

  const aiBlocked = kind === 'ai' && aiStatus !== null && !aiStatus.configured;
  const missing =
    kind === 'pick'
      ? 'Elige una trivia'
      : kind === 'randomFrom'
        ? 'Marca al menos dos trivias'
        : kind === 'ai'
          ? 'Escribe un tema'
          : '';

  const submit = () => {
    if (!source) return;
    onCreate({ source, settings, startAt: startAtFromChoice(start), inviteIds });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-950/80 backdrop-blur-sm flex items-start sm:items-center-safe justify-center p-4 overflow-y-auto"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-match-title"
    >
      <div className="surface-raised bg-ink-900 w-full max-w-xl my-8 animate-pop">
        <div className="flex items-center gap-3 p-5 border-b border-ink-800">
          <span className="w-10 h-10 rounded-xl bg-sky-600/15 text-sky-400 flex items-center justify-center shrink-0">
            <Bot className="w-5 h-5" />
          </span>
          <div className="flex-1 min-w-0">
            <h2 id="create-match-title" className="font-display font-bold text-lg">
              Crear partida
            </h2>
            <p className="text-ink-500 text-xs">
              La dirige Open Dahu: tú juegas como uno más y avanza sola.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-ink-500 hover:text-ink-100 hover:bg-ink-800 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* 1. De dónde sale la trivia */}
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-ink-300 mb-2">¿Qué trivia se juega?</legend>
            <div className="grid grid-cols-2 gap-2">
              {SOURCES.map((s) => (
                <button
                  key={s.kind}
                  type="button"
                  onClick={() => setKind(s.kind)}
                  aria-pressed={kind === s.kind}
                  className={`p-3.5 rounded-xl border-2 text-left transition-colors ${
                    kind === s.kind ? 'bg-sky-600/15 border-sky-500' : 'bg-ink-850 border-ink-700 hover:border-ink-600'
                  }`}
                >
                  <span className={kind === s.kind ? 'text-sky-400' : 'text-ink-400'}>{s.icon}</span>
                  <p className="font-semibold text-ink-50 mt-1 text-sm">{s.title}</p>
                  <p className="text-ink-500 text-xs leading-snug">{s.description}</p>
                </button>
              ))}
            </div>
          </fieldset>

          {/* 2. Detalle según la fuente */}
          {kind === 'pick' && (
            <QuizChooser
              mode="single"
              selected={picked ? [picked] : []}
              onToggle={(q) => setPicked((prev) => (prev?.id === q.id ? null : q))}
            />
          )}

          {kind === 'randomFrom' && (
            <QuizChooser
              mode="multi"
              selected={pool}
              onToggle={(q) =>
                setPool((prev) =>
                  prev.some((p) => p.id === q.id)
                    ? prev.filter((p) => p.id !== q.id)
                    : prev.length >= MAX_RANDOM_POOL
                      ? prev
                      : [...prev, q],
                )
              }
            />
          )}

          {kind === 'random' && (
            <p className="flex items-start gap-2 text-sm text-ink-400 rounded-xl bg-ink-850 border border-ink-700 p-3.5">
              <Dices className="w-4 h-4 text-sky-400 shrink-0 mt-0.5" />
              Se sorteará una trivia pública del catálogo al crear la sala. Todos verán cuál salió antes de empezar.
            </p>
          )}

          {kind === 'ai' && (
            <div className="space-y-3">
              {aiStatus === null ? (
                <div className="h-10 shimmer rounded-lg" />
              ) : aiBlocked ? (
                <div className="rounded-xl border border-ember-600/60 bg-ember-500/5 p-3.5 space-y-2">
                  <p className="text-sm text-ink-200">
                    Para que la IA escriba la trivia necesitas tu clave de Google AI Studio (es gratis).
                  </p>
                  <button
                    type="button"
                    onClick={onConfigureAi}
                    className="flex items-center gap-1.5 text-sm font-semibold text-ember-300 hover:text-ember-200"
                  >
                    <KeyRound className="w-4 h-4" /> Configurar la IA
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <TopicModeButton
                      active={aiTopicMode === 'ours'}
                      onClick={() => setAiTopicMode('ours')}
                      title="Elegimos el tema"
                      description="Escribid sobre qué"
                    />
                    <TopicModeButton
                      active={aiTopicMode === 'theirs'}
                      onClick={() => setAiTopicMode('theirs')}
                      title="Que lo elija la IA"
                      description="Tema sorpresa"
                    />
                  </div>
                  {aiTopicMode === 'ours' && (
                    <input
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      maxLength={200}
                      placeholder="Ej.: la Revolución francesa, el reggaetón, los volcanes…"
                      aria-label="Tema de la trivia"
                      className="w-full h-11 px-3 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 text-sm
                                 placeholder:text-ink-500 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/25"
                    />
                  )}
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm text-ink-400">Cuántas preguntas</span>
                    <NumberPadField
                      value={count}
                      onChange={setCount}
                      min={3}
                      max={30}
                      label="Cuántas preguntas"
                      unit="preguntas"
                    />
                  </div>
                  <p className="text-xs text-ink-500">
                    La sala se abre enseguida y la gente puede ir entrando mientras la IA escribe (suele
                    tardar menos de un minuto). Usa tu clave y la trivia queda guardada en «Mis trivias».
                  </p>
                </>
              )}
            </div>
          )}

          <div className="h-px bg-ink-800" />

          {/* 3. Ajustes de la sala */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <SmallMode
                active={settings.gameMode === 'ffa'}
                onClick={() => set('gameMode', 'ffa')}
                icon={<Trophy className="w-4 h-4" />}
                label="Individual"
              />
              <SmallMode
                active={settings.gameMode === 'teams'}
                onClick={() => set('gameMode', 'teams')}
                icon={<Users className="w-4 h-4" />}
                label="Por equipos"
              />
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm text-ink-400 flex items-center gap-1.5">
                <Users className="w-4 h-4 text-sky-400" /> Hasta
              </span>
              <NumberPadField
                value={settings.maxPlayers}
                onChange={(v) => set('maxPlayers', v)}
                min={MIN_PLAYERS_LIMIT}
                max={MAX_PLAYERS_LIMIT}
                label="Jugadores como máximo"
                unit="jugadores"
              />
            </div>

            <div className="space-y-1">
              <Toggle
                checked={settings.isPublic}
                onChange={(v) => set('isPublic', v)}
                icon={<Globe className="w-4 h-4" />}
                label="Anunciar en la sala de espera"
                hint="Si no, solo se entra con el PIN o por invitación."
              />
              <Toggle
                checked={settings.allowLateJoin}
                onChange={(v) => set('allowLateJoin', v)}
                icon={<DoorOpen className="w-4 h-4" />}
                label="Permitir entrar tarde"
                hint="Quien llegue a mitad empieza desde cero."
              />
              {kind !== 'ai' && (
                <Toggle
                  checked={settings.randomizeQuestions}
                  onChange={(v) => set('randomizeQuestions', v)}
                  icon={<Shuffle className="w-4 h-4" />}
                  label="Barajar preguntas"
                />
              )}
            </div>
          </div>

          <div className="h-px bg-ink-800" />

          {/* 4. Cuándo empieza */}
          <StartTimePicker
            value={start}
            onChange={setStart}
            manualLabel="Al estar listos"
            manualHint="Cada jugador pulsa «Estoy listo». Si lo están todos, empieza a los 3 segundos; si lo está al menos la mitad, a los 30."
          />

          <div className="h-px bg-ink-800" />

          {/* 5. Amigos */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-ink-300">Invitar a amigos a unirse</p>
            <InviteFriendsPicker
              friends={friends}
              selected={inviteIds}
              onChange={setInviteIds}
              exclude={[profile.id]}
              onManageFriends={onManageFriends}
            />
          </div>

          <button
            onClick={submit}
            disabled={!source || aiBlocked}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold bg-ember-500 hover:bg-ember-400 text-on-accent disabled:bg-ink-800 disabled:text-ink-500 transition-colors"
          >
            {kind === 'ai' ? <Sparkles className="w-5 h-5" /> : <Play className="w-5 h-5" fill="currentColor" />}
            {source ? (kind === 'ai' ? 'Crear partida y escribir la trivia' : 'Crear partida') : missing}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */

/**
 * Buscador de trivias del catálogo, con las propias aparte. En modo `single`
 * se marca una; en `multi`, varias para el sorteo.
 */
const QuizChooser: React.FC<{
  mode: 'single' | 'multi';
  selected: QuizSummary[];
  onToggle: (quiz: QuizSummary) => void;
}> = ({ mode, selected, onToggle }) => {
  const [scope, setScope] = useState<'catalog' | 'mine'>('catalog');
  const [q, setQ] = useState('');
  const [results, setResults] = useState<QuizSummary[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    setResults(null);
    const handle = window.setTimeout(async () => {
      try {
        const path =
          scope === 'mine' ? '/quizzes/mine' : q.trim() ? `/quizzes?q=${encodeURIComponent(q.trim())}` : '/quizzes';
        const res = await api.get<{ quizzes: QuizSummary[] }>(path);
        if (!cancelled) setResults(res.quizzes.filter((quiz) => quiz.questionCount > 0));
      } catch {
        if (!cancelled) setResults([]);
      }
    }, q ? 280 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [q, scope]);

  const visible = useMemo(() => {
    if (!results) return null;
    const needle = q.trim().toLowerCase();
    // «Mis trivias» llega entera: el filtro por texto se hace aquí.
    const list =
      scope === 'mine' && needle
        ? results.filter((r) => `${r.title} ${r.category}`.toLowerCase().includes(needle))
        : results;
    return list.slice(0, 60);
  }, [results, q, scope]);

  const isSelected = (id: string) => selected.some((s) => s.id === id);

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500 pointer-events-none" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por tema…"
            aria-label="Buscar trivias"
            type="search"
            className="w-full h-10 pl-9 pr-3 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 text-sm
                       placeholder:text-ink-500 outline-none focus:border-sky-500"
          />
        </div>
        <select
          value={scope}
          onChange={(e) => setScope(e.target.value as 'catalog' | 'mine')}
          aria-label="Dónde buscar"
          className="h-10 px-2 rounded-lg bg-ink-850 border border-ink-700 text-ink-200 text-sm outline-none focus:border-sky-500"
        >
          <option value="catalog">Catálogo</option>
          <option value="mine">Mis trivias</option>
        </select>
      </div>

      {mode === 'multi' && (
        <p className="text-xs text-ink-500">
          {selected.length === 0
            ? 'Marca las trivias entre las que sortear.'
            : `${selected.length} marcadas${selected.length < 2 ? ' · marca al menos otra' : ''}${
                selected.length >= MAX_RANDOM_POOL ? ` · máximo ${MAX_RANDOM_POOL}` : ''
              }`}
        </p>
      )}

      {mode === 'multi' && selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onToggle(s)}
              className="chip border-sky-600 bg-sky-500/10 text-sky-400 hover:bg-sky-500/20"
              aria-label={`Quitar ${s.title}`}
            >
              <span className="font-emoji">{s.coverEmoji}</span>
              <span className="max-w-[10rem] truncate">{s.title}</span>
              <X className="w-3 h-3" />
            </button>
          ))}
        </div>
      )}

      <div className="max-h-60 overflow-y-auto rounded-xl border border-ink-700 divide-y divide-ink-800">
        {visible === null ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-sky-500" />
          </div>
        ) : visible.length === 0 ? (
          <p className="text-ink-500 text-sm text-center py-6">Nada por aquí.</p>
        ) : (
          visible.map((quiz) => {
            const on = isSelected(quiz.id);
            return (
              <button
                key={quiz.id}
                type="button"
                onClick={() => onToggle(quiz)}
                aria-pressed={on}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                  on ? 'bg-sky-500/10' : 'hover:bg-ink-850'
                }`}
              >
                <span className="font-emoji text-2xl leading-none shrink-0" aria-hidden="true">
                  {quiz.coverEmoji}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-ink-100 truncate">{quiz.title}</span>
                  <span className="block text-xs text-ink-500 truncate">
                    {quiz.category} · {quiz.questionCount} {quiz.questionCount === 1 ? 'pregunta' : 'preguntas'}
                  </span>
                </span>
                <span
                  className={`w-5 h-5 shrink-0 flex items-center justify-center border-2 ${
                    mode === 'single' ? 'rounded-full' : 'rounded-md'
                  } ${on ? 'border-sky-500 bg-sky-500 text-white' : 'border-ink-600'}`}
                  aria-hidden="true"
                >
                  {on && <Check className="w-3 h-3" />}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

const TopicModeButton: React.FC<{
  active: boolean;
  onClick: () => void;
  title: string;
  description: string;
}> = ({ active, onClick, title, description }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`p-3 rounded-xl border-2 text-left transition-colors ${
      active ? 'bg-sky-600/15 border-sky-500' : 'bg-ink-850 border-ink-700 hover:border-ink-600'
    }`}
  >
    <p className="font-semibold text-ink-50 text-sm">{title}</p>
    <p className="text-ink-500 text-xs">{description}</p>
  </button>
);

const SmallMode: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}> = ({ active, onClick, icon, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`flex items-center justify-center gap-2 h-11 rounded-xl border-2 text-sm font-semibold transition-colors ${
      active ? 'bg-sky-600/15 border-sky-500 text-ink-50' : 'bg-ink-850 border-ink-700 text-ink-400 hover:border-ink-600'
    }`}
  >
    <span className={active ? 'text-sky-400' : ''}>{icon}</span>
    {label}
  </button>
);

const Toggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  icon: React.ReactNode;
  label: string;
  hint?: string;
}> = ({ checked, onChange, icon, label, hint }) => (
  <label className="flex items-center gap-3 py-2 cursor-pointer">
    <span className={checked ? 'text-sky-400' : 'text-ink-600'}>{icon}</span>
    <span className="flex-1">
      <span className="block text-sm text-ink-100 font-medium">{label}</span>
      {hint && <span className="block text-xs text-ink-500">{hint}</span>}
    </span>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only peer" />
    <span
      aria-hidden="true"
      className={`w-11 h-6 rounded-full p-0.5 transition-colors shrink-0 ${checked ? 'bg-sky-600' : 'bg-ink-700'}
                  peer-focus-visible:ring-2 peer-focus-visible:ring-sky-400`}
    >
      <span className={`block w-5 h-5 rounded-full bg-white transition-transform ${checked ? 'translate-x-5' : ''}`} />
    </span>
  </label>
);
